/**
 * SIDE PANEL: ASSOCIATION
 * Panel latéral pour consulter et modifier une association
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus } from 'lucide-react';
import type { UpdateAssociationInput } from '../../schemas/association';
import { updateAssociationSchema } from '../../schemas/association';
import type { Association } from '../../types/association';
import type { Crew } from '../../types/crew';
import { Form, FormInput, toast } from '../ui';
import { SidePanel, SidePanelFooter } from '../ui/side-panel';
import { CrewsList } from '../crew';
import { useCrews } from '../../hooks/useCrews';
import { usePermissions } from '../../hooks/usePermissions';
import { AssignCrewModal } from '../modals';

interface AssociationSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  association: Association | null;
  onSubmit: (id: string, data: UpdateAssociationInput) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}

export function AssociationSidePanel({
  isOpen,
  onClose,
  association,
  onSubmit,
  onDelete,
}: AssociationSidePanelProps) {
  const { getCrewsByEntityId, updateCrew } = useCrews();
  const { canDeleteEntity } = usePermissions();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [crewsLoading, setCrewsLoading] = useState(false);
  const [isAssignCrewModalOpen, setIsAssignCrewModalOpen] = useState(false);

  const form = useForm<UpdateAssociationInput>({
    resolver: zodResolver(updateAssociationSchema),
  });

  // Réinitialiser le formulaire quand l'association change
  useEffect(() => {
    if (association) {
      form.reset({
        name: association.name,
      });
    }
  }, [association, form]);

  // Charger les équipages associés
  useEffect(() => {
    async function loadCrews() {
      if (!association) return;

      setCrewsLoading(true);
      const data = await getCrewsByEntityId('association', association.id);
      setCrews(data);
      setCrewsLoading(false);
    }

    loadCrews();
  }, [association, getCrewsByEntityId]);

  if (!association) return null;

  const handleFormSubmit = async (data: UpdateAssociationInput) => {
    await onSubmit(association.id, data);
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(association.id);
    }
  };

  const handleAssignCrew = async (crewId: string) => {
    if (!association) return;

    try {
      await updateCrew(crewId, { association_id: association.id });
      toast.success("Équipage assigné à l'association");
      // Recharger les équipages
      const updatedCrews = await getCrewsByEntityId('association', association.id);
      setCrews(updatedCrews);
    } catch (error) {
      console.error('Error assigning crew:', error);
      toast.error("Erreur lors de l'assignation");
    }
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={association.name}
      width="xl"
      onDelete={canDeleteEntity('association') && onDelete ? handleDelete : undefined}
      footer={
        <SidePanelFooter
          onCancel={handleClose}
          submitLabel="Enregistrer"
          submitDisabled={form.formState.isDirty ? false : true}
          isSubmitting={form.formState.isSubmitting}
          formId="association-form"
        />
      }
    >
      <Form {...form}>
        <form
          id="association-form"
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="flex flex-col gap-6"
        >
          {/* Section : Informations générales */}
          <div className="flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-zinc-900">Informations générales</h3>

            <FormInput
              control={form.control}
              name="name"
              label="Nom de l'association"
              required
              placeholder="Ex: Secours Populaire Français"
            />
          </div>

          {/* Section : Équipages associés */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-zinc-900">Équipages associés</h3>
              <button
                type="button"
                onClick={() => setIsAssignCrewModalOpen(true)}
                className="flex items-center justify-center size-9 rounded-md bg-zinc-900 hover:bg-zinc-800 transition-colors"
                aria-label="Assigner un équipage"
                title="Assigner un équipage"
              >
                <Plus className="size-4 text-white" />
              </button>
            </div>
            <CrewsList crews={crews} loading={crewsLoading} />
          </div>
        </form>
      </Form>

      {/* Modale Assigner un équipage */}
      <AssignCrewModal
        isOpen={isAssignCrewModalOpen}
        onClose={() => setIsAssignCrewModalOpen(false)}
        onAssign={handleAssignCrew}
      />
    </SidePanel>
  );
}
